/**
 * Chart Formatter
 *
 * Transforms calculation results into display formats for charts and headlines.
 * This is the presentation layer - it knows nothing about how values were calculated,
 * only how to format them for display.
 */

const ChartFormatter = {

  /**
   * Format for 2-bar comparison chart (Contributions vs Total Value)
   * Used for fund portfolio view showing breakdown by share class
   *
   * @param {Object} result - CalculationResult from a calculator
   * @returns {Object} - { data: Array, labels: Array, classes: Array }
   */
  formatAsComparisonBars: function(result) {
    const breakdown = result.breakdown || [];

    // Calculate total value per class (Capital Account + Distributions + RoC)
    // This shows each class's full performance including realized value
    const classesWithTotalValue = breakdown.map(function(classData) {
      const capitalAccount = classData.value || 0;
      const distributions = classData.distributions || 0;
      const returnsOfCapital = classData.returnsOfCapital || 0;
      const totalClassValue = capitalAccount + distributions + returnsOfCapital;

      return {
        classKey: classData.classKey,
        className: classData.className,
        capitalAccount: capitalAccount,
        distributions: distributions,
        returnsOfCapital: returnsOfCapital,
        totalValue: totalClassValue
      };
    });

    // Filter to only include classes with non-zero total value
    const filteredBreakdown = classesWithTotalValue.filter(function(classData) {
      return classData.totalValue !== 0;
    });

    const classCount = filteredBreakdown.length;

    // Contributions row: [label, contributions, 0, 0, ..., 0]
    // First slot is total contributions, rest are zeros (no class breakdown for contributions)
    const contributionsRow = ['Contributions', result.totalContributions || 0];
    for (let i = 0; i < classCount; i++) {
      contributionsRow.push(0);
    }

    // Total value row: [label, 0, class1TotalValue, class2TotalValue, ...]
    // Each class shows its full value (Capital Account + Distributions + RoC)
    const totalValueRow = ['Total Value', 0];
    filteredBreakdown.forEach(function(classData) {
      totalValueRow.push(classData.totalValue);
    });

    // Labels for legend
    const labels = ['Contributions'];
    filteredBreakdown.forEach(function(classData) {
      labels.push(classData.className || classData.classKey);
    });

    // Class keys for filtering/toggling
    const classes = filteredBreakdown.map(function(c) {
      return c.classKey;
    });

    return {
      data: [contributionsRow, totalValueRow],
      labels: labels,
      classes: classes,
      classDetails: filteredBreakdown,  // Include detailed breakdown for tooltips
      yAxisMin: 0
    };
  },

  /**
   * Format for headline display
   *
   * @param {Object} result - CalculationResult from a calculator
   * @returns {Object} - { primaryValue: string, metrics: Array|null }
   */
  formatAsHeadline: function(result) {
    const formatted = {
      primaryValue: this.formatCurrency(result.totalValue || 0),
      metrics: null
    };

    // Include fund metrics if available
    if (result.fundMetrics) {
      formatted.metrics = [];

      if (typeof result.fundMetrics.tvpi === 'number') {
        formatted.metrics.push({
          label: 'TVPI',
          value: result.fundMetrics.tvpi.toFixed(2) + '×'
        });
      }

      if (typeof result.fundMetrics.dpi === 'number') {
        formatted.metrics.push({
          label: 'DPI',
          value: result.fundMetrics.dpi.toFixed(2) + '×'
        });
      }

      if (typeof result.fundMetrics.rvpi === 'number') {
        formatted.metrics.push({
          label: 'RVPI',
          value: result.fundMetrics.rvpi.toFixed(2) + '×'
        });
      }

      if (typeof result.fundMetrics.moic === 'number') {
        formatted.metrics.push({
          label: 'MOIC',
          value: result.fundMetrics.moic.toFixed(2) + '×'
        });
      }

      // If no valid metrics, set to null
      if (formatted.metrics.length === 0) {
        formatted.metrics = null;
      }
    }

    return formatted;
  },

  /**
   * Format for time series line chart
   * Takes TimeSeriesBuilder output and formats for graphArray
   *
   * @param {Object} timeSeriesResult - Output from TimeSeriesBuilder
   * @returns {Object} - { data: Array, columns: Array, totalValue: number }
   */
  formatAsTimeSeries: function(timeSeriesResult) {
    return {
      data: timeSeriesResult.data || [],
      columns: ['Date', 'Portfolio Value'],
      totalValue: timeSeriesResult.totalValue || 0
    };
  },

  /**
   * Format for portfolio breakdown display
   * Shows Capital Account + Distributions breakdown
   *
   * @param {Object} result - CalculationResult from a calculator
   * @returns {Object} - { capitalAccount, distributions, breakdown }
   */
  formatAsPortfolioBreakdown: function(result) {
    return {
      capitalAccount: {
        label: 'Capital Account',
        value: this.formatCurrency(result.totalCapitalAccount || result.totalValue || 0)
      },
      distributions: {
        label: 'Distributions',
        value: this.formatCurrency(result.totalDistributions || 0)
      },
      totalValue: {
        label: 'Total Value',
        value: this.formatCurrency(result.totalValue || 0)
      },
      breakdown: result.breakdown || []
    };
  },

  /**
   * Format currency value
   * @param {number} amount
   * @returns {string}
   */
  formatCurrency: function(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0';
    }
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  },

  /**
   * Format percentage value
   * @param {number} value - Decimal value (e.g., 0.25 for 25%)
   * @returns {string}
   */
  formatPercent: function(value) {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0%';
    }
    return (value * 100).toFixed(1) + '%';
  },

  /**
   * Format multiplier value (e.g., TVPI)
   * @param {number} value
   * @returns {string}
   */
  formatMultiplier: function(value) {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0.00×';
    }
    return value.toFixed(2) + '×';
  }
};

export { ChartFormatter };
